import {
  AirHandlingUnitData,
  ComponentData,
  moveDown,
  moveUp,
  removeComponent,
} from "@/services/backend/htz/ahu/types/air-handling-unit-data";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import t from "@/lang/lang";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { Button } from "@/shared/components/ui/button";
import { ChevronDown, ChevronUp, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { cn } from "@/shared/lib/utils";
import {
  ComponentAssessments,
  InspectionIndicator,
} from "@/services/backend/htz/ahu/components/indicators";
import { TableCard } from "@/shared/components/tables/table-card";

export function AhuComponentsTable({
  ahu,
  onAhuChange,
  disabled,
}: {
  ahu: AirHandlingUnitData;
  onAhuChange: (ahu: AirHandlingUnitData) => void;
  disabled?: boolean;
}) {
  return (
    <TableCard className="h-full overflow-auto">
      <BuildModeTable ahu={ahu} onAhuChange={onAhuChange} disabled={disabled} />
    </TableCard>
  );
}

/**
 * AhuComponentsInspectionTable lists the components of the air handling
 * unit with their assessments. A component is selected to be inspected.
 */
export function AhuComponentsInspectionTable({
  ahu,
  activeComponent,
  setActiveComponent,
  assessments,
}: {
  ahu: AirHandlingUnitData;
  activeComponent: ComponentData | null;
  setActiveComponent: (component: ComponentData | null) => void;
  assessments: ComponentAssessments;
}) {
  return (
    <TableCard className="h-full overflow-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("")}</TableHead>
            <TableHead>{t("Name")}</TableHead>
            <TableHead>{t("Details")}</TableHead>
            <TableHead>{t("Inspektionen")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {[...ahu.components].sort(byOrder).map((component) => (
            <TableRow
              key={component.id}
              className={cn(
                "cursor-pointer",
                component.id === activeComponent?.id && "bg-muted",
              )}
              onClick={() => {
                if (component.id === activeComponent?.id) {
                  setActiveComponent(null);
                } else {
                  setActiveComponent(component);
                }
              }}
            >
              <TableCell>
                <Checkbox
                  checked={component.id === activeComponent?.id}
                  onCheckedChange={(checked) => {
                    if (checked === true) {
                      setActiveComponent(component);
                    } else {
                      setActiveComponent(null);
                    }
                  }}
                />
              </TableCell>
              <TableCell>{component.name}</TableCell>
              <TableCell>{component.data.details}</TableCell>
              <TableCell className="py-0">
                <InspectionIndicator
                  componentId={component.componentId}
                  assessments={assessments}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableCard>
  );
}

function BuildModeTable({
  ahu,
  onAhuChange,
  disabled,
}: {
  ahu: AirHandlingUnitData;
  onAhuChange: (ahu: AirHandlingUnitData) => void;
  disabled?: boolean;
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("Abfolge")}</TableHead>
          <TableHead>{t("Name")}</TableHead>
          <TableHead>{t("Details")}</TableHead>
          <TableHead>{t("")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {[...ahu.components].sort(byOrder).map((component) => (
          <TableRow key={component.id}>
            <TableCell className="py-0">
              <div className="flex w-12 flex-col space-y-1">
                <Button
                  className="h-4"
                  variant="outline"
                  disabled={disabled}
                  onClick={() => onAhuChange(moveUp(ahu, component))}
                >
                  <ChevronUp />
                </Button>
                <Button
                  className="h-4"
                  variant="outline"
                  disabled={disabled}
                  onClick={() => onAhuChange(moveDown(ahu, component))}
                >
                  <ChevronDown />
                </Button>
              </div>
            </TableCell>
            <TableCell>{component.name}</TableCell>
            <TableCell>{component.data.details}</TableCell>
            <TableCell>
              <DeleteAhuComponentDialog
                component={component}
                disabled={disabled}
                onDelete={() => {
                  onAhuChange(removeComponent(ahu, component.id));
                }}
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function byOrder(a: ComponentData, b: ComponentData) {
  const diff = a.order - b.order;
  switch (true) {
    case diff < 0:
      return -1;
    case diff > 0:
      return 1;
    default:
      return 0;
  }
}

function DeleteAhuComponentDialog({
  component,
  onDelete,
  disabled,
}: {
  component: ComponentData;
  onDelete: () => void;
  disabled?: boolean;
}) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button size="sm" variant="destructive" disabled={disabled}>
          <Trash2 className="h-4 w-5" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {component.name} {t("löschen?")}
          </DialogTitle>
          <DialogDescription>
            {t("Bestätigen entfernt die Komponente von der Anlage.")}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("Abbrechen")}</Button>
          </DialogClose>
          <Button variant="destructive" onClick={onDelete}>
            {t("Löschen")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
